import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { fmtCurrencyExact, fmtDate } from "@/lib/constants";
import { Wallet, Plus, Trash2, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div>
    <Label className="stamp-label text-slate-500">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const TYPE_STYLE = {
  material: "bg-cyan-50 text-cyan-700 border-cyan-200",
  labour: "bg-amber-50 text-amber-700 border-amber-200",
  other: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function JobBudgetDialog({ job, open, onClose }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({ budgeted_labour_cost: "", budgeted_materials_cost: "", material_lead_days: "" });
  const [confirmPick, setConfirmPick] = useState("");
  const [entry, setEntry] = useState({ date: new Date().toLocaleDateString("en-CA"), type: "material", description: "", man_days: "", amount: "" });

  const { data: defaults, refetch: refetchDefaults } = useQuery({
    queryKey: ["job-budget-defaults", job.id],
    queryFn: async () => (await api.get(`/jobs/${job.id}/budget-defaults`)).data,
    enabled: open,
  });
  const { data: ledger, refetch: refetchLedger } = useQuery({
    queryKey: ["job-expenses", job.id],
    queryFn: async () => (await api.get(`/jobs/${job.id}/expenses`)).data,
    enabled: open,
  });
  const rate = ledger?.man_day_rate || defaults?.man_day_rate || 0;

  useEffect(() => {
    if (open) {
      setDraft({
        budgeted_labour_cost: job.budgeted_labour_cost ?? "",
        budgeted_materials_cost: job.budgeted_materials_cost ?? "",
        material_lead_days: job.material_lead_days ?? "",
      });
      setEntry({ date: new Date().toLocaleDateString("en-CA"), type: "material", description: "", man_days: "", amount: "" });
    }
  }, [open, job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["job", job.id] });
    qc.invalidateQueries({ queryKey: ["job-expenses", job.id] });
    qc.invalidateQueries({ queryKey: ["job-budget-defaults", job.id] });
  };

  const confirmQuote = useMutation({
    mutationFn: async (quoteId) => (await api.post(`/jobs/${job.id}/confirm-quote`, { quote_id: quoteId })).data,
    onSuccess: (d) => { toast.success(`${d.quote_number} marked as the accepted quote`); invalidate(); refetchDefaults(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not confirm quote"),
  });
  const saveBudget = useMutation({
    mutationFn: async () => (await api.patch(`/jobs/${job.id}`, {
      budgeted_labour_cost: draft.budgeted_labour_cost === "" ? null : (parseFloat(draft.budgeted_labour_cost) || 0),
      budgeted_materials_cost: parseFloat(draft.budgeted_materials_cost) || 0,
      material_lead_days: draft.material_lead_days === "" ? null : Math.max(0, parseInt(draft.material_lead_days, 10) || 0),
    })).data,
    onSuccess: () => { toast.success("Budget saved"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save budget"),
  });
  const addEntry = useMutation({
    mutationFn: async () => {
      const body = {
        date: entry.date, type: entry.type, description: entry.description.trim(),
        man_days: entry.type === "labour" ? (parseFloat(entry.man_days) || 0) : null,
        amount: entry.amount === "" ? null : (parseFloat(entry.amount) || 0),
      };
      return (await api.post(`/jobs/${job.id}/expenses`, body)).data;
    },
    onSuccess: () => {
      toast.success("Expense added to the ledger");
      setEntry((e) => ({ ...e, description: "", man_days: "", amount: "" }));
      invalidate(); refetchLedger();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add expense"),
  });
  const delEntry = useMutation({
    mutationFn: async (eid) => (await api.delete(`/jobs/${job.id}/expenses/${eid}`)).data,
    onSuccess: () => { toast.success("Entry removed"); invalidate(); refetchLedger(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove entry"),
  });
  const reassignType = useMutation({
    mutationFn: async ({ eid, type }) => (await api.patch(`/jobs/${job.id}/expenses/${eid}/type`, { type })).data,
    onSuccess: (_d, vars) => {
      toast.success(vars.type === "labour" ? "PO now counts as labour cost (contractor)" : "Entry now counts as material cost");
      invalidate(); refetchLedger();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not reassign the entry"),
  });

  const useDefaults = () => {
    setDraft((d) => ({
      ...d,
      budgeted_labour_cost: defaults?.suggested_labour_cost ?? d.budgeted_labour_cost,
      budgeted_materials_cost: defaults?.suggested_materials_cost ?? d.budgeted_materials_cost,
    }));
    toast.success(`Budget defaults pulled from ${defaults?.quote?.quote_number || "the accepted quote"}`);
  };

  const labourPreview = entry.type === "labour" && entry.amount === ""
    ? (parseFloat(entry.man_days) || 0) * rate : null;
  const entries = ledger?.entries || [];
  const totals = ledger?.totals || { materials: 0, labour: 0, man_days: 0, total: 0 };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm w-[calc(100vw-16px)] sm:max-w-3xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="job-budget-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Wallet size={18} className="text-cyan-600" /> Budget &amp; Costs — {job.job_number || job.title}
          </DialogTitle>
        </DialogHeader>

        {defaults && !defaults.accepted && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="no-accepted-quote-warning">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
              <AlertTriangle size={15} /> No quote is marked as accepted for this job
            </div>
            {defaults.candidates?.length ? (
              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                <Select value={confirmPick} onValueChange={setConfirmPick}>
                  <SelectTrigger data-testid="confirm-quote-select" className="h-9 rounded-sm bg-white w-full sm:flex-1 min-w-0">
                    <SelectValue placeholder="Which quote was accepted?" />
                  </SelectTrigger>
                  <SelectContent>
                    {defaults.candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.quote_number} · {fmtCurrencyExact(c.sale_price || 0)} · {c.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button data-testid="confirm-quote-btn" size="sm" disabled={!confirmPick || confirmQuote.isPending}
                  onClick={() => confirmQuote.mutate(confirmPick)}
                  className="rounded-sm h-9 bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto shrink-0">
                  <CheckCircle2 size={14} className="mr-1.5" /> Confirm accepted
                </Button>
              </div>
            ) : (
              <p className="text-xs text-amber-700 mt-1">No quotes are linked to this job's deal — set the budget manually below.</p>
            )}
          </div>
        )}
        {defaults?.accepted && (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 flex items-center gap-2" data-testid="accepted-quote-banner">
            <CheckCircle2 size={14} /> Accepted quote: <b>{defaults.quote.quote_number}</b> · {fmtCurrencyExact(defaults.quote.sale_price || 0)} · man-day rate {fmtCurrencyExact(rate)}
          </div>
        )}

        <div className="border border-slate-200 rounded-sm p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="stamp-label text-slate-600">Budget</div>
            {defaults?.accepted && (
              <Button data-testid="use-quote-defaults-btn" size="sm" variant="outline" onClick={useDefaults} className="rounded-sm h-8 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50">
                <Sparkles size={13} className="mr-1.5" /> Use quote defaults ({fmtCurrencyExact(defaults.suggested_labour_cost || 0)} labour · {fmtCurrencyExact(defaults.suggested_materials_cost)} materials)
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Budgeted labour cost $">
              <Input type="number" step="50" data-testid="budget-labour-cost" value={draft.budgeted_labour_cost}
                placeholder={job.labour_budget ? String(job.labour_budget) : "0"}
                onChange={(e) => setDraft({ ...draft, budgeted_labour_cost: e.target.value })} />
            </Field>
            <Field label="Budgeted materials cost">
              <Input type="number" step="0.01" data-testid="budget-materials" value={draft.budgeted_materials_cost} onChange={(e) => setDraft({ ...draft, budgeted_materials_cost: e.target.value })} />
            </Field>
            <Field label="Material lead time (days)">
              <Input type="number" min="0" max="365" step="1" data-testid="budget-lead-days" value={draft.material_lead_days} placeholder="e.g. 14"
                onChange={(e) => setDraft({ ...draft, material_lead_days: e.target.value })} />
            </Field>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-[10px] text-slate-400">
              {job.planner_labour?.mandays_tasks?.length > 0
                ? <>Leave labour blank to use the planner's crew billing automatically ({fmtCurrencyExact(job.planner_labour.cost)} scheduled).</>
                : "Leave labour blank to fall back to the planner's crew billing when tasks are costed."}
            </p>
            <Button data-testid="save-budget-btn" size="sm" disabled={saveBudget.isPending} onClick={() => saveBudget.mutate()} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white shrink-0">
              {saveBudget.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Save budget
            </Button>
          </div>
        </div>

        <div className="border border-slate-200 rounded-sm p-3">
          <div className="stamp-label text-slate-600 mb-1">Expense ledger — actual costs</div>
          <p className="text-[10px] text-slate-400 mb-2">
            Actual costs come from this ledger. Entries marked AUTO come from POs linked to this job (ex GST) and the Project planner's crew billing — they update automatically as POs and tasks change. PO entries land as material — use the type dropdown on the row to count a contractor PO as labour instead.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-[110px_110px_1fr_90px_100px_70px] gap-1.5 items-end">
            <Field label="Date"><Input type="date" data-testid="ledger-date" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} className="h-9 rounded-sm text-xs" /></Field>
            <Field label="Type">
              <Select value={entry.type} onValueChange={(v) => setEntry({ ...entry, type: v, man_days: "", amount: "" })}>
                <SelectTrigger data-testid="ledger-type" className="h-9 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="labour">Labour</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description"><Input data-testid="ledger-desc" value={entry.description} onChange={(e) => setEntry({ ...entry, description: e.target.value })} placeholder={entry.type === "labour" ? "e.g. 2 crew × 1 day — site install" : "e.g. Bunnings — fixings"} className="h-9 rounded-sm text-xs" /></Field>
            {entry.type === "labour" ? (
              <Field label="Man-days"><Input type="number" step="0.5" data-testid="ledger-man-days" value={entry.man_days} onChange={(e) => setEntry({ ...entry, man_days: e.target.value })} className="h-9 rounded-sm text-xs" /></Field>
            ) : <div />}
            <Field label={entry.type === "labour" ? "$ (override)" : "Amount $"}>
              <Input type="number" step="0.01" data-testid="ledger-amount" value={entry.amount}
                placeholder={labourPreview != null ? labourPreview.toFixed(2) : ""}
                onChange={(e) => setEntry({ ...entry, amount: e.target.value })} className="h-9 rounded-sm text-xs" />
            </Field>
            <Button data-testid="ledger-add-btn" size="sm"
              disabled={addEntry.isPending || !entry.description.trim() || (entry.type === "labour" ? !entry.man_days && entry.amount === "" : entry.amount === "")}
              onClick={() => addEntry.mutate()}
              className="rounded-sm h-9 bg-slate-800 hover:bg-slate-900 text-white">
              <Plus size={14} />
            </Button>
          </div>

          <div className="mt-3 border border-slate-100 rounded-sm divide-y divide-slate-100 max-h-56 overflow-y-auto">
            {entries.map((r) => (
              <div key={r.id} className="flex items-center gap-2 px-2.5 py-2 text-xs" data-testid={`ledger-row-${r.id}`}>
                <span className="text-slate-400 w-[74px] shrink-0">{fmtDate(r.date)}</span>
                {r.source === "po" ? (
                  <Select value={r.type === "labour" ? "labour" : "material"} onValueChange={(v) => reassignType.mutate({ eid: r.id, type: v })}>
                    <SelectTrigger data-testid={`ledger-type-${r.id}`} title="Contractor PO? Count it as labour instead of material"
                      className={`h-5 w-auto gap-1 rounded-sm border text-[9px] px-1.5 py-0 shrink-0 font-medium ${TYPE_STYLE[r.type] || TYPE_STYLE.other}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="material" className="text-xs">material</SelectItem>
                      <SelectItem value="labour" className="text-xs">labour (contractor)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`rounded-sm border text-[9px] px-1.5 shrink-0 hover:bg-inherit ${TYPE_STYLE[r.type] || TYPE_STYLE.other}`}>{r.type}</Badge>
                )}
                <span className="flex-1 text-slate-700 truncate">{r.description}</span>
                {(r.source === "po" || r.source === "planner") && <Badge className="rounded-sm bg-slate-800 text-white text-[8px] px-1 shrink-0 hover:bg-slate-800" title={r.source === "planner" ? "From the Project planner's crew billing" : "From a linked PO"}>AUTO</Badge>}
                {r.type === "labour" && r.man_days != null && <span className="text-slate-400 shrink-0">{r.man_days}d</span>}
                <span className="font-mono font-semibold text-slate-900 shrink-0">{fmtCurrencyExact(r.amount || 0)}</span>
                {r.source !== "po" && r.source !== "planner" ? (
                  <button data-testid={`ledger-del-${r.id}`} onClick={() => { if (window.confirm(`Remove "${r.description}" (${fmtCurrencyExact(r.amount || 0)}) from the ledger?`)) delEntry.mutate(r.id); }} className="text-slate-400 hover:text-rose-600 shrink-0" title="Remove entry"><Trash2 size={13} /></button>
                ) : <span className="w-3 shrink-0" />}
              </div>
            ))}
            {entries.length === 0 && <div className="p-5 text-center text-xs text-slate-400">No expenses yet — add material or labour costs above, or link a PO to this job.</div>}
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs" data-testid="ledger-totals">
            <span className="text-slate-500">Labour: <b className="font-mono text-slate-900">{fmtCurrencyExact(totals.labour)}</b> ({totals.man_days}d)</span>
            <span className="text-slate-500">Materials &amp; other: <b className="font-mono text-slate-900">{fmtCurrencyExact(totals.materials)}</b></span>
            <span className="text-slate-700 font-semibold">Total actual: <b className="font-mono">{fmtCurrencyExact(totals.total)}</b></span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
